import { useCallback, useEffect, useState } from 'react'
import { PREFERENCE, readPreference, writePreference } from './prefs'

/**
 * The interface's accent: buttons, links, selection, focus and the tint of
 * card borders all follow it. Presets rather than a free picker, so every
 * choice keeps text at AA (4.5:1 or more) on both themes. None is green,
 * red or amber, which mean pass, fail and conflict here.
 */
export interface AccentScheme {
  id: string
  name: string
  /** Light theme: the accent on white. Dark theme: the accent on near-black. */
  light: string
  dark: string
}

export const ACCENTS: AccentScheme[] = [
  { id: 'everse', name: 'EVERSE purple', light: '#80529a', dark: '#c39bd8' },
  { id: 'magenta', name: 'Magenta', light: '#a3206f', dark: '#f29bd0' },
  // The logo's blue (--brand-blue, #3ea3dc) as is in dark mode. On light
  // surfaces it is 2.7:1, too faint for text, so a deeper shade of the same hue.
  { id: 'everse-blue', name: 'EVERSE blue', light: '#176a99', dark: '#3ea3dc' },
  { id: 'navy', name: 'Navy', light: '#1f3a7a', dark: '#9db8f2' },
  { id: 'cyan', name: 'Cyan', light: '#0b6a85', dark: '#6ad0ee' },
  { id: 'teal', name: 'Teal', light: '#0f6e6e', dark: '#5cc8c8' },
  { id: 'steel', name: 'Steel', light: '#3d5a80', dark: '#a9c1dc' },
  { id: 'graphite', name: 'Graphite', light: '#3f4a5a', dark: '#b8c2d0' },
]

export const DEFAULT_ACCENT = 'everse'
const PROPERTIES = ['--accent', '--accent-text', '--accent-quiet', '--focus'] as const

function apply(id: string): void {
  const root = document.documentElement
  const scheme = ACCENTS.find((a) => a.id === id)
  // The default is the stylesheet's own; high contrast keeps its own, strongest accent.
  if (!scheme || id === DEFAULT_ACCENT || root.dataset.contrast === 'high') {
    for (const property of PROPERTIES) root.style.removeProperty(property)
    return
  }
  const dark = root.dataset.theme === 'dark'
  const accent = dark ? scheme.dark : scheme.light
  root.style.setProperty('--accent', accent)
  root.style.setProperty('--focus', accent)
  root.style.setProperty('--accent-text', dark ? '#0b0f19' : '#ffffff')
  root.style.setProperty(
    '--accent-quiet',
    `color-mix(in srgb, ${accent} ${dark ? '16%' : '9%'}, var(--surface-raised))`,
  )
}

export function useAccent(): [string, (id: string) => void] {
  const [id, setId] = useState<string>(() => readPreference(PREFERENCE.accent) ?? DEFAULT_ACCENT)

  useEffect(() => {
    apply(id)
    // Each accent has a value per theme, and high contrast sets it aside.
    const observer = new MutationObserver(() => apply(id))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-contrast'],
    })
    return () => observer.disconnect()
  }, [id])

  const set = useCallback((next: string) => {
    setId(next)
    writePreference(PREFERENCE.accent, next)
  }, [])

  return [id, set]
}
